import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/pricing", label: "Pricing" },
  { to: "/audit", label: "Free Audit" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dash = user?.role === "admin" ? "/admin" : "/portal";

  return (
    <header
      data-testid="public-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#141414]/80 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 font-display text-lg font-bold tracking-tight text-[#F3EDDC]">
          <img src="/logo.png" alt="WebLaunch Studio" className="h-8 w-8 rounded-md object-cover ring-1 ring-[#C9A227]/40" />
          WebLaunch<span className="text-[#C9A227]">.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-[#C9A227] ${isActive ? "text-[#C9A227]" : "text-[#D2CAB2]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <Button data-testid="nav-dashboard-btn" onClick={() => navigate(dash)} className="rounded-full bg-black hover:bg-[#242424] text-white">
              Dashboard <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")} className="text-[#D2CAB2]">
                Log in
              </Button>
              <Button data-testid="nav-cta-btn" onClick={() => navigate("/build")} className="gold-shimmer-btn rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black">
                Build My Website
              </Button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden text-[#F3EDDC]" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#141414] border-t border-white/10 px-4 py-4 space-y-2">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-[#E6DEC7] font-medium">
              {l.label}
            </NavLink>
          ))}
          <div className="pt-2 flex gap-2">
            {user ? (
              <Button className="w-full rounded-full bg-black text-white" onClick={() => { setOpen(false); navigate(dash); }}>Dashboard</Button>
            ) : (
              <>
                <Button variant="outline" className="w-full rounded-full" onClick={() => { setOpen(false); navigate("/login"); }}>Log in</Button>
                <Button className="w-full rounded-full bg-[#C9A227] text-black" onClick={() => { setOpen(false); navigate("/pricing"); }}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="footer" className="bg-black text-[#B7AF98] mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 font-display text-lg font-bold text-white">
            <img src="/logo.png" alt="WebLaunch Studio" className="h-8 w-8 rounded-md object-cover ring-1 ring-[#C9A227]/40" />
            WebLaunch
          </div>
          <p className="mt-4 text-sm text-[#7E7663] max-w-xs">Professional websites that help local businesses grow.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/portfolio" className="hover:text-white">Portfolio</Link></li>
            <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Get Started</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/register" className="hover:text-white">Client Portal</Link></li>
            <li><Link to="/login" className="hover:text-white">Log in</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Contact</h4>
          <ul className="space-y-2 text-sm text-[#7E7663]">
            <li>hello@weblaunchstudio.com</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-[#948C79]">
        © {new Date().getFullYear()} WebLaunch Studio. Portfolio items are fictional concept projects.
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#141414] text-[#F3EDDC]">
      <PublicNav />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
